import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Sparkles, KeyRound, ArrowLeft } from "lucide-react";

export default function ForgotPassword() {
  const { forgotPassword, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tokenFromUrl = params.get("token") || "";

  const [email, setEmail] = useState("");
  const [token, setToken] = useState(tokenFromUrl);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const stage = token ? "reset" : "request";

  const requestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await forgotPassword(email);
      if (data.reset_token) {
        // No email provider configured — we advance straight to the reset step.
        setToken(data.reset_token);
        setParams({ token: data.reset_token });
        toast.success("Reset link created. Set your new password below.");
      } else {
        toast.success("If that email exists, a reset link has been created.");
      }
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const doReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await resetPassword(token, password);
      toast.success("Password updated! Please log in.");
      navigate("/login");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center px-5 py-10">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="w-full max-w-md bg-white rounded-3xl border border-gray-100 shadow-soft p-8">
        <Link to="/login" className="flex items-center gap-2 mb-6">
          <span className="w-9 h-9 rounded-2xl bg-primary flex items-center justify-center text-white"><Sparkles size={18} /></span>
          <span className="font-heading font-extrabold text-xl tracking-tight">Errandell</span>
        </Link>

        <span className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-4"><KeyRound size={22} /></span>
        <h1 className="font-heading font-extrabold text-3xl text-[#1A1D20] mb-1">
          {stage === "request" ? "Forgot password?" : "Set a new password"}
        </h1>
        <p className="text-sm text-[#6B7280] mb-6">
          {stage === "request"
            ? "Enter your email and we'll create a secure reset link."
            : "Choose a new password for your account."}
        </p>

        {stage === "request" ? (
          <form onSubmit={requestReset} className="space-y-4">
            <div>
              <Label className="font-semibold text-sm">Email</Label>
              <Input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@email.com"
                className="mt-1.5 rounded-2xl h-12 border-gray-200" />
            </div>
            <Button data-testid="forgot-submit-btn" type="submit" disabled={loading}
              className="w-full rounded-full h-12 font-bold bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">
              {loading ? "Please wait…" : "Create reset link"}
            </Button>
          </form>
        ) : (
          <form onSubmit={doReset} className="space-y-4">
            <div>
              <Label className="font-semibold text-sm">New password</Label>
              <Input data-testid="reset-password-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="At least 6 characters"
                className="mt-1.5 rounded-2xl h-12 border-gray-200" />
            </div>
            <Button data-testid="reset-submit-btn" type="submit" disabled={loading}
              className="w-full rounded-full h-12 font-bold bg-primary hover:bg-[#FF8D70] active:scale-95 transition-transform">
              {loading ? "Please wait…" : "Update password"}
            </Button>
          </form>
        )}

        <Link to="/login" data-testid="forgot-back-link" className="flex items-center justify-center gap-1 text-sm font-semibold text-[#6B7280] hover:text-primary mt-6">
          <ArrowLeft size={15} /> Back to log in
        </Link>
      </motion.div>
    </div>
  );
}
